import type { Holding, Portfolio } from '@/hooks/portfolio/usePortfolio';
import { useIsMobileScreen } from '@/hooks/useMedia';
import loadable from '@loadable/component';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';
import { Table } from '@/components/NarrativeTable/components/Table';
import { useSortedHoldings } from '../../hooks/useHoldingsSorting';
import { usePanelHoldingId } from '../../hooks/usePanelHoldingId';
import { TableTitle } from '@/components/NarrativeTable';
import { PortfolioPanel } from '../PortfolioPanel';
import { PortfolioTableActions } from '@/components/NarrativeTable/components/TableActions';
import { usePortfolioPeriod } from '@/components/NarrativeTable/hooks';
import { Period } from '@/utilities/timeSeriesPeriodMap';
import { WatchlistItem } from '@/hooks/useWatchlist';
import {
  DemoPrimaryNarrative,
  PrimaryNarrative,
} from '@/components/NarrativeTable/components/PrimaryNarrative';
import { getTradingItemCurrencyISO } from '@/utilities/getTradingItemCurrencyISO';
import { v4 as uuidv4 } from 'uuid';
import {
  getRandomDisplayName,
  getRandomName,
  getRandomNarrativeTitle,
} from './utils';
import { NarrativesBanner } from '@/components/NarrativeTable/components/NarrativesBanner';
import { useOnboardingBanner } from '@/components/NarrativeTable/hooks/useOnboardingBanner';

interface Props {
  portfolio: Portfolio;
  primaryNarrative?: ({
    item,
  }: {
    item: Holding | WatchlistItem;
  }) => React.ReactNode;
}

const RowDesktop = loadable(
  () => import('@/components/NarrativeTable/components/TableRow'),
  {
    resolveComponent: (c) => c.RowDesktop,
  }
);
const RowExpanded = loadable(
  () => import('@/components/NarrativeTable/components/TableRow'),
  {
    resolveComponent: (c) => c.RowExpanded,
  }
);
const RowMobile = loadable(
  () => import('@/components/NarrativeTable/components/TableRow'),
  {
    resolveComponent: (c) => c.RowMobile,
  }
);

const PortfolioNarrativeTableRow = ({
  item,
  index,
  isMobileScreen,
  panelHoldingId,
  setPanelHoldingId,
  portfolioPeriod,
  primaryNarrative,
}: {
  item: Holding;
  index: number;
  isMobileScreen: boolean;
  panelHoldingId: string | null;
  setPanelHoldingId: (id: string | null) => void;
  portfolioPeriod: Period;
  primaryNarrative?: React.ReactNode;
}) => {
  const isSelected = panelHoldingId === item.id;

  if (isMobileScreen) {
    return (
      <RowMobile
        key={item.id}
        selected={isSelected}
        index={index}
        item={item}
        onItemSelect={setPanelHoldingId}
        period={portfolioPeriod}
        primaryNarrative={primaryNarrative}
      />
    );
  }

  if (panelHoldingId) {
    return (
      <RowDesktop
        key={item.id}
        selected={isSelected}
        index={index}
        item={item}
        onItemSelect={setPanelHoldingId}
        period={portfolioPeriod}
        primaryNarrative={primaryNarrative}
      />
    );
  }

  return (
    <RowExpanded
      key={item.id}
      index={index}
      listItem={item}
      onItemSelect={setPanelHoldingId}
      period={portfolioPeriod}
      primaryNarrative={primaryNarrative}
    />
  );
};

export const NarrativePanel = ({ portfolio, primaryNarrative }: Props) => {
  const { isMobile } = useUserAgent();
  const isMobileScreen = useIsMobileScreen({ defaultMatches: isMobile });
  const sortedHoldings = useSortedHoldings(portfolio.holdings);
  const defaultHoldingId = sortedHoldings[0]?.id ?? null;
  const [panelHoldingId, setPanelHoldingId] = usePanelHoldingId({
    defaultId: defaultHoldingId,
  });

  const [portfolioPeriod] = usePortfolioPeriod();

  const { isBannerDismissed, handleBannerDismissed } = useOnboardingBanner();

  const handleToggleTableExpansion = () => {
    setPanelHoldingId((id) => (id ? null : defaultHoldingId));
  };

  const hasSelectedNarrative = portfolio.holdings.some(
    (holding) => holding.company.myValuation
  );

  return (
    <>
      <div
        className={`grid gap-x2 ${
          panelHoldingId
            ? 'lg:grid-cols-[auto_32%]'
            : 'lg:grid-cols-[auto_auto]'
        }`}
      >
        <div>
          <div className="grid grid-cols-[auto_auto] justify-between py-2">
            <div className={isMobileScreen ? 'pl-4' : ''}>
              <TableTitle itemCount={sortedHoldings.length} title="Holdings" />
            </div>
            <PortfolioTableActions
              onToggleTableExpansion={handleToggleTableExpansion}
            />
          </div>
          {!isBannerDismissed && !hasSelectedNarrative && (
            <NarrativesBanner
              content="Select your fair value and track your investment with narratives."
              handleBannerDismissed={handleBannerDismissed}
            />
          )}
          <Table itemId={panelHoldingId}>
            {sortedHoldings.map((item, index) => (
              <PortfolioNarrativeTableRow
                key={item.id}
                item={item}
                index={index}
                isMobileScreen={isMobileScreen}
                panelHoldingId={panelHoldingId}
                setPanelHoldingId={setPanelHoldingId}
                portfolioPeriod={portfolioPeriod}
                primaryNarrative={
                  primaryNarrative ? (
                    primaryNarrative({ item: item as Holding })
                  ) : (
                    <PrimaryNarrative
                      companyId={item.company.id}
                      companyName={item.company.name}
                      currencyISO={
                        getTradingItemCurrencyISO(
                          item.company.data.currencyInfo
                        ) ?? 'XXX'
                      }
                      myValuation={item.company.myValuation}
                      valuationOptions={item.company.valuationOptions}
                      tickerSymbol={item.company.tickerSymbol}
                      isOnboardingNarrative={
                        !isBannerDismissed &&
                        !hasSelectedNarrative &&
                        index === 0
                      }
                    />
                  )
                }
              />
            ))}
          </Table>
        </div>
        {panelHoldingId && (
          <PortfolioPanel
            holdingId={panelHoldingId}
            holdings={portfolio.holdings}
            setHoldingId={setPanelHoldingId}
            excludedHoldings={[]}
          />
        )}
      </div>
    </>
  );
};

export const DemoNarrativePanel = ({ portfolio }: Pick<Props, 'portfolio'>) => {
  const demoPortfolioMock = {
    ...portfolio,
    holdings: portfolio.holdings.map((holding) => {
      const valuationTypes = ['analysts', 'user_defined', 'dcf'];
      const randomIndex = Math.floor(Math.random() * valuationTypes.length);
      const selectedType = valuationTypes[randomIndex];

      const fallbackValuation = {
        id: uuidv4(),
        fairValue: Math.random() * 150,
        type: 'user_defined',
        narrative: {
          author: {
            id: uuidv4(),
            displayName: getRandomDisplayName(),
            name: getRandomName(),
            imageUrl: '',
          },
          id: uuidv4(),
          updatedAt: new Date().toISOString(),
          title: getRandomNarrativeTitle(),
        },
      };

      const selectedValuation =
        holding.company.valuationOptions &&
        holding.company.valuationOptions.length > 0
          ? {
              id: uuidv4(),
              ...(holding.company.valuationOptions.find(
                (option) =>
                  option.type.toLowerCase() === selectedType.toLowerCase()
              ) ?? fallbackValuation),
            }
          : fallbackValuation;

      return {
        ...holding,
        company: {
          ...holding.company,
          myValuation: selectedValuation,
        },
      };
    }),
  };

  return (
    <NarrativePanel
      portfolio={demoPortfolioMock}
      primaryNarrative={({ item }) => (
        <DemoPrimaryNarrative
          companyId={item.company.id}
          companyName={item.company.name}
          currencyISO={
            getTradingItemCurrencyISO(item.company.data.currencyInfo) ?? 'XXX'
          }
          myValuation={item.company.myValuation}
          valuationOptions={item.company.valuationOptions}
          tickerSymbol={item.company.tickerSymbol}
        />
      )}
    />
  );
};
